import { matchPath } from 'react-router-dom';

import { SCREENS } from './screens';
import type { Screen } from './screens';
import type { NavItem } from './navItems';

/**
 * Whether somebody holding these products may reach a row that names those.
 *
 * <h2>One definition, because there are two callers</h2>
 *
 * `AppSidebar.isVisible` asks it to decide whether to draw the row; `ProductRoute` asks it
 * to decide whether to render the screen behind it. Written twice, the two drift — and the
 * way they drift is the worst of the available bugs: a row that is hidden but reachable, or
 * one that is drawn and then refuses.
 *
 * <h2>Any-of, not all-of</h2>
 *
 * "Xtremand AI Agent" fronts `seo_ai_agent` and `linkedin_ai_agent`, and holding either is a
 * reason to want the row — it is a picker standing in front of two agents. All-of would hide
 * a product somebody had paid for because they had not paid for its neighbour.
 *
 * <h2>A row naming no product is for everybody</h2>
 *
 * Dashboard, Team and My Account are backed by nothing sold. Whether
 * those are restricted at all is a question about ROLES, which this function does not ask
 * and must not: two gates answering one question is how a rule becomes impossible to state.
 *
 * `docs/features/navigation.md` NV-4 to NV-6.
 */
export function holdsProductFor(item: NavItem | Screen, productKeys: ReadonlySet<string>): boolean {
  if (!item.productKeys?.length) {
    return true;
  }
  return item.productKeys.some((key) => productKeys.has(key));
}

/**
 * The screen a path belongs to, if any.
 *
 * <p>How `ProductRoute` knows which product to demand: it guards a group of routes rather
 * than one, so it has to look up what it is standing in front of.
 *
 * <p><b>It searches `SCREENS`, not the menu — and that is the fix of 8 October 2026.</b> It
 * read `NAV_ITEMS` until then, which tied the guard to the sidebar: taking a row out of the
 * rail removed the product check in front of its screen, silently. Three screens have no row
 * today and all three are still guarded.
 *
 * <p><b>Matched with `matchPath`, and still never as a prefix.</b> It compared strings until
 * 8 October 2026, which worked for every screen then registered and silently could not guard
 * a **parameterised** one: `/email-warmup/account/:externalId` is not equal to
 * `/email-warmup/account/<a uuid>`, so the lookup would have returned undefined and the
 * caller treats undefined as unguarded. The first such screen would have slipped through
 * with nothing to report it.
 *
 * <p>`matchPath` defaults to `end: true`, so the property that mattered is unchanged and was
 * verified against this version of the router: `/team` does **not** match `/team/add`,
 * `/agents` does not match `/agents/seo`, and `/email-warmup` does not match
 * `/email-warmup/account/x`. Those are separate registrations with different gates — any-of
 * over both agents, and the SEO agent alone — and a prefix match would silently extend a
 * screen's products over every path beneath it.
 *
 * <p>The first match wins, so a path matching two patterns takes the earlier registration.
 * Nothing registered today is ambiguous; a literal and a pattern that could both match one
 * address would be a registry to fix rather than an order to rely on.
 *
 * @returns the screen, or undefined for a path that is not a registered screen — the
 *          assistant, a collection, the profile and billing pages — which the caller treats
 *          as unguarded
 */
export function screenForPath(pathname: string): Screen | undefined {
  return SCREENS.find((screen) => matchPath(screen.route, pathname) !== null);
}
